// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { ProductImagePrecheck } from "@/api/hooks/use-product-images";

/** Image extensions the backend accepts in a batch. */
export const ACCEPTED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "jfif", "png", "gif", "tif"] as const;

/** `accept` attribute of the file picker, e.g. ".jpg,.jpeg,…". */
export const ACCEPTED_IMAGE_ACCEPT = ACCEPTED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(",");

/** Merge newly picked files into the selection; a file picked again under the same name replaces the old one. */
export function mergeSelectedFiles(current: File[], added: File[]): File[] {
  const byName = new Map(current.map((file) => [file.name, file]));
  for (const file of added) byName.set(file.name, file);
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Names of the files a precheck mapped to a product; only those get uploaded after approval. */
export function mappedFileNames(precheck: ProductImagePrecheck): Set<string> {
  return new Set(precheck.files.filter((file) => file.validation === "mapped").map((file) => file.fileName));
}

/** Run `work` over `items` with at most `limit` calls in flight; rejections are passed to `onError` and never stop the run. */
export async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  work: (item: T) => Promise<void>,
  onError: (item: T, error: unknown) => void,
): Promise<void> {
  const queue = [...items];
  async function worker() {
    for (let item = queue.shift(); item !== undefined; item = queue.shift()) {
      try {
        await work(item);
      } catch (error) {
        onError(item, error);
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}
